import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  UsersIcon, 
  LightbulbIcon, 
  LeafIcon, 
  HeartHandshakeIcon,
  BuildingIcon,
  ArrowRightIcon,
  TargetIcon
} from '../components/icons/Icons';
import { useSettings } from '../context/SettingsContext';
import './Impact.css';

const Impact = () => {
  const { settings } = useSettings();
  const impactAreas = [
    {
      icon: UsersIcon,
      title: 'Economic Inclusion & Job Creation',
      description: 'Promoting economic inclusion and creating employment opportunities across our investment sectors, empowering individuals and families throughout Zambia.'
    },
    {
      icon: BuildingIcon,
      title: 'Essential Services Access',
      description: 'Enhancing access to essential services such as education, healthcare, and clean energy, ensuring communities have the resources they need to thrive.'
    },
    {
      icon: LightbulbIcon,
      title: 'Innovation & Entrepreneurship',
      description: 'Driving innovation and supporting local entrepreneurship, fostering a culture of creativity and business development across the nation.'
    },
    {
      icon: LeafIcon,
      title: 'Environmental Sustainability',
      description: 'Championing sustainable practices that protect our environment and natural resources for future generations.'
    }
  ];

  const sdgGoals = [
    { number: '1', title: 'No Poverty' },
    { number: '2', title: 'Zero Hunger' },
    { number: '8', title: 'Decent Work' },
    { number: '9', title: 'Industry & Innovation' },
    { number: '12', title: 'Responsible Consumption' },
    { number: '13', title: 'Climate Action' }
  ];

  return (
    <>
      <Helmet>
        <title>Impact & Community | Fuwali Investments Zambia Limited</title>
        <meta name="description" content="Discover how Fuwali Investments creates lasting positive change through impact-driven investments aligned with Zambia's development goals and the SDGs." />
        <link rel="canonical" href="https://fuwalizambia.com/impact" />
        <meta property="og:title" content="Impact & Community | Fuwali Investments Zambia Limited" />
        <meta property="og:description" content="Discover how Fuwali Investments creates lasting positive change through impact-driven investments." />
        <meta property="og:url" content="https://fuwalizambia.com/impact" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Page Hero */}
      <section className="page-hero" style={settings.hero_bg_impact ? { backgroundImage: `url(${settings.hero_bg_impact})` } : {}}>
        <div className="page-hero-overlay"></div>
        <div className="container">
          <span className="page-hero-label">Impact & Community</span>
          <h1 className="page-hero-title">The Vision</h1>
          <p className="page-hero-subtitle">
            Creating lasting, positive change in the lives of people and the environment we operate in.
          </p>
        </div>
      </section>

      {/* Vision Statement */}
      <section className="section vision-statement">
        <div className="container">
          <div className="vision-statement-content">
            <div className="vision-icon-large">
              <HeartHandshakeIcon size={60} />
            </div>
            <h2>Our Commitment to Community Impact</h2>
            <p>
              At Fuwali Investments Zambia Limited, our commitment to community impact is deeply rooted in our vision — to be a leading force in fostering innovative, impact-driven investment solutions that uplift communities and contribute to national progress.
            </p>
            <p>
              We believe that true investment success goes beyond financial returns. It must create lasting, positive change in the lives of people and the environment we operate in. That's why our approach integrates sustainability at every level, aligning with Zambia's national development goals and the broader Sustainable Development Goals (SDGs).
            </p>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section impact-areas-section">
        <div className="container">
          <span className="section-label center">Our Focus</span>
          <h2 className="section-title">Strategic Impact Areas</h2>
          <p className="section-subtitle">
            Through strategic partnerships and diversified investments, we aim to support initiatives that create meaningful change.
          </p>
          <div className="impact-areas-grid">
            {impactAreas.map((area, index) => (
              <div key={index} className="impact-area-card">
                <div className="impact-area-icon">
                  <area.icon size={32} />
                </div>
                <h3>{area.title}</h3>
                <p>{area.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SDG Alignment */}
      <section className="section sdg-section">
        <div className="container">
          <div className="sdg-content">
            <div className="sdg-text">
              <span className="section-label">Global Alignment</span>
              <h2 className="section-title text-left">Sustainable Development Goals</h2>
              <p className="sdg-description">
                Our investment strategy aligns with the United Nations Sustainable Development Goals, ensuring our work contributes to global efforts for a better, more sustainable future.
              </p>
              <p className="sdg-description">
                By integrating these goals into our operations, we ensure that every investment decision considers its broader impact on society and the environment.
              </p>
            </div>
            <div className="sdg-goals">
              {sdgGoals.map((goal, index) => (
                <div key={index} className="sdg-goal">
                  <span className="sdg-number">SDG {goal.number}</span>
                  <span className="sdg-title">{goal.title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section approach-section">
        <div className="container">
          <div className="approach-grid">
            <div className="approach-card">
              <TargetIcon size={40} />
              <h3>Strategic Partnerships</h3>
              <p>Collaborating with organizations that share our vision for sustainable development and community empowerment.</p>
            </div>
            <div className="approach-card">
              <LightbulbIcon size={40} />
              <h3>Diversified Investments</h3>
              <p>Spreading our investments across sectors to maximize impact and create resilient economic growth.</p>
            </div>
            <div className="approach-card">
              <HeartHandshakeIcon size={40} />
              <h3>Community First</h3>
              <p>Placing community needs at the center of our investment decisions to ensure meaningful, lasting impact.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Join Our Impact Journey</h2>
            <p>Partner with us to create sustainable change in Zambia's communities.</p>
            <Link to="/contact" className="btn btn-secondary">
              Get Involved <ArrowRightIcon size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Impact;
